import { BadgeCard, ContextMenu } from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, MouseEvent } from 'react';
import { useTeachingBlockCardMenu } from '../../../hooks/useTeachingBlockCardMenu';
import { isBadgeCardWidth } from '../../../utils/typeguards';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { TeachingBlockCardType } from '../types';
import styles from './TeachingBlockCard.module.scss';

export interface TeachingBlockCardProps {
  card: TeachingBlockCardType;
  passive: boolean;
  duration?: number;
  count?: number;
  onHover?: (isHovering: boolean, card: TeachingBlockCardType) => void;
  onClick?: (event: MouseEvent<HTMLDivElement>, card: TeachingBlockCardType) => void;
  className?: string | undefined;
}

export const TeachingBlockCard: FC<TeachingBlockCardProps> = observer(
  ({ card, count = 1, duration, passive, onHover, onClick, className }) => {
    const store = useTeachingBlockStore();

    const realDuration = duration ? Math.ceil(duration / 7) : (card.duration ?? 1);

    const { menu } = useTeachingBlockCardMenu({
      card,
      styles,
      hasWarnings: false,
      hasAvailabilityWarnings: false,
      isPicked: store.pickedCard?.card === card,
      isPinned: !!card.pinned,
    });

    const wrapperClasses = classNames(
      styles['teaching-block-card'],
      {
        [styles['is-passive']]: passive,
        [styles['is-multi']]: count >= 1,
        [styles['is-picked']]: store.pickedCard?.card.uuid === card.uuid,
        [styles['is-pinned']]: card.pinned,
        [styles['is-locked']]: card.locked,
        [styles['has-conflict']]:
          store.highlightMode.includes('warnings') &&
          ((card.availabilityWarnings && card.availabilityWarnings.length > 0) ||
            (card.warnings && card.warnings.length > 0)),
      },
      className,
    );

    return (
      <>
        <ContextMenu className={styles.menu} data={menu} parent={document.getElementById('teaching-block-grid')}>
          <div
            className={wrapperClasses}
            title={card.label}
            onClick={(e) => {
              !card.loading && onClick && onClick(e, card);
            }}
            onMouseEnter={() => onHover && onHover(true, card)}
            onMouseLeave={() => onHover && onHover(false, card)}
          >
            <BadgeCard
              count={count}
              color={card.badgeCardTextColor}
              width={isBadgeCardWidth(realDuration) ? realDuration : 6}
              label={card.subject?.shortName}
              rows={card.badgeCardRows}
              showHover
            />
          </div>
        </ContextMenu>
      </>
    );
  },
);
